import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { AuthenticationService } from "../../../../../auth/service";
import { locale as italian } from "../../../../../i18n/it";
import { CitiesService } from "../../../../../auth/service/cities.service";
import nationsWithCodes from "app/auth/models/nations-with-codes";
import { IContentHeader } from "app/layout/components/content-header/content-header.component";
import { ICity } from "app/auth/models/cities";
import { ICustomer, ICustomerAddressForm, ICustomerAddressList } from "../../../customer.interface";
import { CustomerService } from "../../../customer.service";

@Component({
  selector: "app-customers-site-edit-form",
  templateUrl: "./customers-site-edit-form.component.html",
  styleUrls: ["./customers-site-edit-form.component.scss"],
})
export class CustomersSiteEditFormComponent implements OnInit {
  public formGroup: FormGroup;
  public contentHeader: IContentHeader = null;
  public address_id: number;
  public customers: ICustomer[] = [];
  public previousSiteAddress: ICustomerAddressForm;
  private showErrors: boolean = false;
  public nationsWithCodes: { name: string; code: string }[] = nationsWithCodes;

  constructor(
    public _utilService: UtilsService,
    public _activatedRoute: ActivatedRoute,
    public _authenticationService: AuthenticationService,
    public customerService: CustomerService,
    private _formBuilder: FormBuilder,
    private router: Router,
    public _citiesService: CitiesService,
  ) {
    this._activatedRoute.paramMap.subscribe(async (params: ParamMap) => {
      const addressId: string = params.get("id");

      if (addressId) {
        if (!this._authenticationService.hasCapability("edit_customer_address")) {
          this._utilService.hideLoading();
          await this.router.navigateByUrl("/dashboard/main");
          return false;
        }
        this._utilService.disableAutoHideLoading();
        this.address_id = Number(addressId);
        await this.loadAddressData();
      }
    });

    this.contentHeader = {
      headerTitle: "",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/",
          },
          {
            name: italian.data.CUSTOMERS,
            isLink: false,
          },
          {
            name: italian.data.MENU.CUSTOMERS_LOCATION,
            isLink: true,
            link: "/dashboard/customers-location",
          },
          {
            name: italian.data.EDIT,
            isLink: false,
          },
        ],
      },
    };
  }

  async ngOnInit() {
    this.formGroup = this._formBuilder.group({
      client: new FormControl("", [Validators.required, Validators.min(1)]),
      label: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      street: new FormControl("", [Validators.required, Validators.minLength(2), Validators.maxLength(255)]),
      zipcode: new FormControl("", [Validators.required, Validators.minLength(4), Validators.maxLength(5)]),
      city: new FormControl("", [Validators.required, Validators.min(1)]),
      external_city: new FormControl(""),
      province: new FormControl("", [Validators.required, Validators.min(1)]),
      km: new FormControl(0, [Validators.required, Validators.min(0)]),
      external: new FormControl(false),
      country: new FormControl(null),
    });

    this.customers = await this.customerService.getCustomers();
  }

  private async loadAddressData() {
    const address: ICustomerAddressList = await this.customerService.getAddress(this.address_id);
    this.contentHeader.headerTitle = address.client.name + " - " + address.label;
    this.formGroup.patchValue({
      client: address.client.id,
      label: address.label,
      street: address.street,
      zipcode: address.zipcode,
      city: address.city_id,
      external_city: address.external_city,
      province: address.province_id,
      km: address.km,
      external: Boolean(address.external),
      country: address.country,
    });
    if (this.formGroup.value.external) {
      this.formGroup.get("country").addValidators(Validators.required);
      this.formGroup.get("external_city").addValidators(Validators.required);
    } else {
      this.formGroup.get("country").clearValidators();
      this.formGroup.get("external_city").clearValidators();
    }
    this._utilService.hideLoading();
    this.previousSiteAddress = this.formGroup.value;
  }

  fieldHasErrors(field_name: string): boolean {
    if (this.showErrors) {
      return this.formGroup.controls[field_name].status === "INVALID";
    } else {
      return this.formGroup.controls[field_name].status === "INVALID" && this.formGroup.controls[field_name].touched;
    }
  }

  /*In caso di sede estera città zipcode e provincia saranno impostati e inviati a null*/
  manageExternalSite() {
    if (this.formGroup.value.external) {
      this.formGroup.patchValue({
        zipcode: null,
        city: null,
        province: null,
        km: 1,
      });
      this.formGroup.get("country").addValidators(Validators.required);
      this.formGroup.get("external_city").addValidators(Validators.required);
    } else {
      this.formGroup.patchValue({
        zipcode: null,
        city: null,
        province: null,
        km: null,
      });
      this.formGroup.get("country").clearValidators();
      this.formGroup.get("external_city").clearValidators();
    }
  }

  setAutoProvince() {
    const cityValue: number = this.formGroup.get("city").value;
    if (cityValue > 0) {
      const cityObj: ICity = this._citiesService.list.find((city: ICity) => city.id === cityValue);
      const province = this._citiesService.list.find((city: ICity) => city.initials === cityObj.initials && city.is_main === 1);
      this.formGroup.patchValue({ province: province.id });
    }
  }

  async edit() {
    if (!this._authenticationService.hasCapability("edit_customer_address")) {
      window.history.back();
      return false;
    }
    this.showErrors = true;
    // controllo valori
    if (!this.formGroup.value.external) {
      if (this.formGroup.invalid) {
        return false;
      }
    } else {
      if (!this.formGroup.value.client || !this.formGroup.value.label || !this.formGroup.value.street) {
        return false;
      }
    }
    let data: ICustomerAddressForm = this.formGroup.value;
    this._utilService.showLoading();
    return this.customerService
      .editAddress(this.address_id, data)
      .then(async () => {
        //In caso il valore di km sia a ZERO calcoliamo in automatico la distanza della sede dalla nostra
        if (data.km === 0) {
          await this.customerService
            .calculateDistanceForCustomerToUs(this.address_id)
            .then(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALSuccess({ text: "SUCCESS_CALCULATE_DISTANCE_CUSTOMER" });
            })
            .catch(async () => {
              this._utilService.hideLoading();
              await this._utilService.showSWALError({ text: "ERROR_CALCULATE_DISTANCE_CUSTOMER" });
            });
        } else {
          //Controllo se i dati della sede siano cambiati, se si cambiamo il calcolo della distanza
          if (
            data.street !== this.previousSiteAddress.street ||
            data.city !== this.previousSiteAddress.city ||
            data.province !== this.previousSiteAddress.province ||
            (data.zipcode !== this.previousSiteAddress.zipcode && this.previousSiteAddress.km === data.km)
          ) {
            await this.customerService
              .calculateDistanceForCustomerToUs(this.address_id)
              .then(async () => {
                this._utilService.hideLoading();
                await this._utilService.showSWALSuccess({ text: "SUCCESS_CALCULATE_DISTANCE_CUSTOMER" });
              })
              .catch(async () => {
                this._utilService.hideLoading();
                await this._utilService.showSWALError({ text: "ERROR_CALCULATE_DISTANCE_CUSTOMER" });
              });
          }
        }
        this._utilService.hideLoading();
        await this._utilService.showSWALSuccess({ text: "SUCCESS_UPDATE_CUSTOMER_SITE" });
        await this.router.navigateByUrl("/dashboard/customers-location");
      })
      .catch(async () => {
        this._utilService.hideLoading();
        await this._utilService.showSWALError({ text: "ERROR_UPDATE_CUSTOMER_SITE" });
      });
  }
}
